import { readBundledPcfConfigGrpc } from '../../../shared/api/grpc/pcfBundledSettingsClient';
import { applyPipeexSettingsToConfigurationStore } from '../../../shared/api/pipeex/pcfSettingsDtoApply';
import { saveCurrentConfigurationToPipeex } from '../../../shared/api/pipeex/pcfSettingsRemote';
import { buildPcfSettingsDtoFromStore } from '../../../shared/api/pipeex/pcfSettingsDtoBuilder';
import { setActivePipeexProfile } from '../../../shared/api/pipeex/pcfSettingsActiveProfile';
import { SettingsType } from '../../../shared/api/pipeex/pcfSettingsTypes';
import { getProfileKey } from '../../../shared/api/pipeex/pcfSettingsSerialization';
import {
  markResumeIntoMappingIntent,
  markLiveWizardMappingHandoffIntent,
} from '../../../shared/lib/workflowResumeIntent';
import { useProjectSetupHistoryStore } from '../../../shared/stores/useProjectSetupHistoryStore';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { useUIStore } from '../../../shared/stores/useUIStore';
import { webLog } from '../../../shared/api/grpc/webLogger';
import { buildLegacySettingsProfile } from './legacySettingsProfile';

const PROFILE_NAME = 'Default (Legacy)';

export interface ConvertBundledSettingsInput {
  userId: string | null | undefined;
  activeProjectKey: string | null;
  /** Injected trust-binding writer to avoid cross-feature imports. */
  bindProfileToProject?: (profileKey: string, origin: 'legacy-conversion') => void;
}

export type ConvertBundledSettingsResult =
  | { success: true }
  | { success: false; error: string };

/**
 * Reads the five bundled PcfConfig JSON files from the plugin via gRPC and
 * merges them with the current Zustand store state (wizard progress, bolt
 * config, connector binding, workflow options, QuickFill, etc.).
 *
 * The bundled files only carry the 5 core data sections (advanced settings,
 * all tags, BOM parameters, ISO parameters, validation map). The store state
 * carries everything the user configured during previous sessions. Merging
 * both ensures the converted profile is a complete snapshot.
 */
export async function convertBundledSettingsToProfile(
  input: ConvertBundledSettingsInput
): Promise<ConvertBundledSettingsResult> {
  try {
    const grpcResult = await readBundledPcfConfigGrpc();
    if (!grpcResult.success || !grpcResult.settings) {
      const error = grpcResult.error ?? 'Unknown gRPC error';
      webLog.error('convertSettings', `gRPC read failed: ${error}`);
      return { success: false, error };
    }

    const currentStoreDto = buildPcfSettingsDtoFromStore();
    const profile = buildLegacySettingsProfile(currentStoreDto, grpcResult.settings);
    if (!profile.success) {
      webLog.error('convertSettings', profile.error);
      return profile;
    }

    // Save before applying so a remote failure cannot partially replace the
    // user's active workspace. The supplied DTO keeps custom legacy rows exact.
    await saveCurrentConfigurationToPipeex(
      PROFILE_NAME,
      SettingsType.User,
      profile.settings
    );

    applyPipeexSettingsToConfigurationStore(profile.settings);
    autoEnableProcessFlagsFromBomParameters(profile.settings.bomParameters);
    ensureSelectedOptionIsSet();

    setActivePipeexProfile(PROFILE_NAME, SettingsType.User);

    const profileKey = getProfileKey(PROFILE_NAME, SettingsType.User);
    input.bindProfileToProject?.(profileKey, 'legacy-conversion');

    // Restore the newly saved profile while identifying this as a conversion,
    // not a wizard completion. Mapping uses that distinction to skip setup
    // readiness checks that cannot be reconstructed from legacy files.
    markResumeIntoMappingIntent();
    markLiveWizardMappingHandoffIntent();
    useProjectSetupHistoryStore.getState().markCompleted(input.userId, input.activeProjectKey);

    return { success: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    webLog.error('convertSettings', `Conversion failed: ${message}`);
    return { success: false, error: message };
  }
}

/**
 * Inspects the raw BomParameters JSON from the bundled PcfConfig and enables
 * the process-conditions / project-properties sections in the configuration
 * store when non-empty entries exist. Called after the profile apply so the
 * mapping page renders these sections as active when the user lands there
 * after conversion.
 */
function autoEnableProcessFlagsFromBomParameters(bomParametersJson: string): void {
  let bom: Record<string, unknown>;
  try {
    bom = JSON.parse(bomParametersJson);
  } catch {
    return;
  }

  const systemParameters = Array.isArray(bom.SystemParameters) ? bom.SystemParameters : [];
  const projectParameters = Array.isArray(bom.ProjectParameters) ? bom.ProjectParameters : [];

  if (systemParameters.length === 0 && projectParameters.length === 0) {
    return;
  }

  const store = useConfigurationStore.getState();
  const processConfig = store.processPropertiesConfiguration;

  const nextProcessConditionsChecked =
    systemParameters.length > 0 ? true : processConfig.processConditionsChecked;
  const nextProjectPropertiesChecked =
    projectParameters.length > 0 ? true : processConfig.projectPropertiesChecked;

  if (
    nextProcessConditionsChecked !== processConfig.processConditionsChecked ||
    nextProjectPropertiesChecked !== processConfig.projectPropertiesChecked
  ) {
    store.loadConfiguration({
      processPropertiesConfiguration: {
        ...processConfig,
        processConditionsChecked: nextProcessConditionsChecked,
        projectPropertiesChecked: nextProjectPropertiesChecked,
      },
    });
  }
}

const DEFAULT_WORKFLOW_OPTION = 'Iso Drawings Generation';

/**
 * Ensures the UI store has a workflow option selected. The mapping page's
 * live-wizard-handoff gate requires `Boolean(selectedOption)` to recognize
 * the arrival as a verified profile handoff. When the user converts their
 * existing settings without going through the wizard menu, the option may
 * be empty — this fills it with the default workflow so the gate bypasses.
 */
function ensureSelectedOptionIsSet(): void {
  const uiStore = useUIStore.getState();
  if (!uiStore.selectedOption) {
    uiStore.setSelectedOption(DEFAULT_WORKFLOW_OPTION);
  }
}
